import React, { useState, useRef, useEffect } from 'react'

interface WalkAndTalkProps {
  onClose: () => void
  onTranscribed: (text: string) => void
}

type WalkState = 'ready' | 'recording' | 'transcribing' | 'done' | 'error'

const WALK_PROMPTS = [
  "How has your day been so far? Just speak freely — no one is listening except you.",
  "What's been on your mind today that you haven't had a chance to sit with yet?",
  "If you could say one honest thing about how you're really feeling right now, what would it be?",
  "What happened today that you want to remember — good or difficult?",
  "What's weighing on you right now? Just say it out loud.",
  "How's your energy today compared to yesterday?",
  "What would feel like a win for you by the end of today?",
]

export default function WalkAndTalk({ onClose, onTranscribed }: WalkAndTalkProps) {
  const [walkState, setWalkState] = useState<WalkState>('ready')
  const [seconds, setSeconds] = useState(0)
  const [prompt] = useState(
    WALK_PROMPTS[Math.floor(Math.random() * WALK_PROMPTS.length)]
  )
  const [errorMessage, setErrorMessage] = useState('')

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
    }
  }, [])

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60)
    const s = secs % 60
    return m + ':' + (s < 10 ? '0' : '') + s
  }

  const startWalking = async () => {
    setErrorMessage('')
    audioChunksRef.current = []

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data)
      }

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop())
        await handleTranscribe()
      }

      mediaRecorder.start(100)
      setWalkState('recording')
      setSeconds(0)

      timerRef.current = setInterval(() => {
        setSeconds(prev => {
          if (prev >= 300) {
            stopWalking()
            return prev
          }
          return prev + 1
        })
      }, 1000)

    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setErrorMessage('Microphone access denied. Please allow access in your browser settings.')
      } else {
        setErrorMessage('Could not access microphone. Please try again.')
      }
    }
  }

  const stopWalking = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop()
      setWalkState('transcribing')
    }
  }

  const handleTranscribe = async () => {
    if (audioChunksRef.current.length === 0) {
      setWalkState('error')
      setErrorMessage('No audio recorded. Please try again.')
      return
    }

    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
      const formData = new FormData()
      formData.append('audio', audioBlob, 'walk-recording.webm')

      const token = localStorage.getItem('velumio_token')
      const response = await fetch((process.env.REACT_APP_API_URL || 'http://localhost:3000') + '/entries/voice', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token },
        body: formData,
      })

      const data = await response.json()

      if (data.transcription) {
        setWalkState('done')
        setTimeout(() => {
          onTranscribed(data.transcription)
          onClose()
        }, 2000)
      } else {
        setWalkState('error')
        setErrorMessage('Could not transcribe. Please try speaking more clearly.')
      }
    } catch (err) {
      setWalkState('error')
      setErrorMessage('Transcription failed. Please try again.')
    }
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 9999,
      backgroundColor: '#0F1B2D',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px',
    }}>

      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: '24px', right: '24px',
          backgroundColor: 'rgba(255,255,255,0.1)',
          border: 'none', color: 'rgba(248,244,238,0.7)',
          fontSize: '24px', width: '44px', height: '44px',
          borderRadius: '50%', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          lineHeight: 1,
        }}
      >
        ×
      </button>

      {/* Logo */}
      <div style={{
        position: 'absolute', top: '28px', left: '32px',
        fontFamily: 'Georgia, serif', fontSize: '20px',
        fontWeight: 700, color: '#F8F4EE',
      }}>
        Velu<span style={{ color: '#C8963E' }}>mio</span>
      </div>

      {/* READY STATE */}
      {walkState === 'ready' && (
        <div style={{ textAlign: 'center', maxWidth: '500px' }}>
          <div style={{ fontSize: '72px', marginBottom: '24px' }}>🚶</div>
          <h2 style={{
            fontFamily: 'Georgia, serif', fontSize: '32px',
            color: '#F8F4EE', marginBottom: '16px', fontWeight: 600,
          }}>
            Walk & Talk
          </h2>
          <p style={{
            fontSize: '16px', color: 'rgba(248,244,238,0.6)',
            lineHeight: 1.7, marginBottom: '24px',
          }}>
            Go for a walk and journal out loud. Velumio listens,
            transcribes, and saves your thoughts automatically.
          </p>
          <p style={{
            fontSize: '16px', fontStyle: 'italic',
            fontFamily: 'Georgia, serif',
            color: '#C8963E', marginBottom: '40px',
            lineHeight: 1.7, padding: '0 16px',
          }}>
            "{prompt}"
          </p>
          <button
            onClick={startWalking}
            style={{
              padding: '18px 56px', fontSize: '17px',
              fontWeight: 600, color: '#0F1B2D',
              backgroundColor: '#C8963E', border: 'none',
              borderRadius: '999px', cursor: 'pointer',
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            Start walking
          </button>
          {errorMessage && (
            <p style={{ marginTop: '20px', fontSize: '13px', color: '#FCA5A5' }}>
              {errorMessage}
            </p>
          )}
          <p style={{
            marginTop: '40px', fontSize: '12px',
            color: 'rgba(248,244,238,0.25)', lineHeight: 1.6,
          }}>
            Your recording is transcribed and immediately deleted.
            Only the text is saved — encrypted and private to you.
          </p>
        </div>
      )}

      {/* RECORDING STATE */}
      {walkState === 'recording' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '120px', height: '120px', borderRadius: '50%',
            backgroundColor: 'rgba(159,18,57,0.15)',
            border: '3px solid #9F1239',
            display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 32px',
            animation: 'pulse 1.5s ease infinite',
          }}>
            <span style={{ fontSize: '48px' }}>🎤</span>
          </div>

          <p style={{
            fontFamily: 'Georgia, serif', fontSize: '22px',
            color: '#F8F4EE', marginBottom: '12px',
          }}>
            Listening...
          </p>

          <p style={{
            fontSize: '48px', fontFamily: 'monospace',
            color: '#C8963E', marginBottom: '16px', fontWeight: 700,
          }}>
            {formatTime(seconds)}
          </p>

          <p style={{
            fontSize: '15px', color: 'rgba(248,244,238,0.5)',
            marginBottom: '48px', fontStyle: 'italic',
            fontFamily: 'Georgia, serif',
          }}>
            Speak freely — take your time
          </p>

          <button
            onClick={stopWalking}
            style={{
              padding: '16px 48px', fontSize: '16px',
              fontWeight: 600, color: '#FFFFFF',
              backgroundColor: '#9F1239', border: 'none',
              borderRadius: '999px', cursor: 'pointer',
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            I'm done talking
          </button>

          <p style={{
            marginTop: '16px', fontSize: '12px',
            color: 'rgba(248,244,238,0.3)',
          }}>
            Maximum 5 minutes
          </p>
        </div>
      )}

      {/* TRANSCRIBING STATE */}
      {walkState === 'transcribing' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '72px', height: '72px',
            border: '4px solid rgba(248,244,238,0.15)',
            borderTopColor: '#C8963E', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 32px',
          }} />
          <p style={{
            fontFamily: 'Georgia, serif', fontSize: '24px',
            color: '#F8F4EE', marginBottom: '12px',
          }}>
            Transcribing your walk...
          </p>
          <p style={{ fontSize: '14px', color: 'rgba(248,244,238,0.5)' }}>
            Whisper AI is converting your voice to text
          </p>
        </div>
      )}

      {/* DONE STATE */}
      {walkState === 'done' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '90px', height: '90px', borderRadius: '50%',
            backgroundColor: 'rgba(45,106,79,0.2)',
            border: '3px solid #2D6A4F',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 28px', fontSize: '40px',
          }}>
            ✓
          </div>
          <p style={{
            fontFamily: 'Georgia, serif', fontSize: '26px',
            color: '#F8F4EE', marginBottom: '10px',
          }}>
            Walk saved
          </p>
          <p style={{ fontSize: '15px', color: 'rgba(248,244,238,0.5)' }}>
            Your thoughts have been transcribed and saved
          </p>
        </div>
      )}

      {/* ERROR STATE */}
      {walkState === 'error' && (
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <p style={{
            fontSize: '22px', color: '#F8F4EE',
            fontFamily: 'Georgia, serif', marginBottom: '12px',
          }}>
            Something went wrong
          </p>
          <p style={{ fontSize: '14px', color: '#FCA5A5', marginBottom: '32px' }}>
            {errorMessage}
          </p>
          <button
            onClick={() => { setWalkState('ready'); setErrorMessage('') }}
            style={{
              padding: '14px 40px', fontSize: '15px',
              fontWeight: 500, color: '#0F1B2D',
              backgroundColor: '#C8963E', border: 'none',
              borderRadius: '999px', cursor: 'pointer',
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            Try again
          </button>
        </div>
      )}

    </div>
  )
}